/**
 * Browser voice: dictation through the Web Speech API (Safari and Chrome; on iPhone it uses the
 * system recogniser) and spoken replies through speechSynthesis. Both are optional: when the
 * browser has no recogniser the microphone button is hidden and typing works as always.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Language } from './lib/i18n.ts';
import { dictationText, pickVoice, recognitionLang } from './lib/voice.ts';

// The Web Speech recogniser is not in TypeScript's DOM library; this is the part we use.
interface RecognitionResult {
  readonly isFinal: boolean;
  readonly 0: { readonly transcript: string };
}
interface RecognitionEvent {
  readonly resultIndex: number;
  readonly results: { readonly length: number; readonly [index: number]: RecognitionResult };
}
interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: ((event: { readonly error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}
type RecognitionConstructor = new () => Recognition;

function recognitionConstructor(): RecognitionConstructor | null {
  const w = window as unknown as {
    SpeechRecognition?: RecognitionConstructor;
    webkitSpeechRecognition?: RecognitionConstructor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export type VoiceProblem = 'denied' | 'failed' | null;

export function useDictation(language: Language, onText: (text: string) => void) {
  const [listening, setListening] = useState(false);
  const [problem, setProblem] = useState<VoiceProblem>(null);
  const recognition = useRef<Recognition | null>(null);
  const supported = recognitionConstructor() !== null;

  const stop = useCallback(() => {
    recognition.current?.stop();
  }, []);

  const start = useCallback(
    (before: string) => {
      const Ctor = recognitionConstructor();
      if (Ctor === null) return;
      const r = new Ctor();
      r.lang = recognitionLang(language);
      r.continuous = true;
      r.interimResults = true;
      let finalWords = '';
      r.onresult = (event) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result === undefined) continue;
          if (result.isFinal) finalWords = `${finalWords} ${result[0].transcript}`;
          else interim += result[0].transcript;
        }
        onText(dictationText(before, finalWords, interim));
      };
      r.onerror = (event) => {
        setProblem(
          event.error === 'not-allowed' || event.error === 'service-not-allowed'
            ? 'denied'
            : 'failed',
        );
      };
      r.onend = () => {
        setListening(false);
        recognition.current = null;
      };
      setProblem(null);
      recognition.current = r;
      r.start();
      setListening(true);
    },
    [language, onText],
  );

  useEffect(() => () => recognition.current?.stop(), []);

  return { supported, listening, problem, start, stop };
}

/** Reads an answer aloud in the app language with the best local voice available. */
export function speak(text: string, language: Language): void {
  if (!('speechSynthesis' in window) || text.trim() === '') return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = recognitionLang(language);
  const voice = pickVoice(window.speechSynthesis.getVoices(), language);
  if (voice) utterance.voice = voice;
  window.speechSynthesis.speak(utterance);
}

export function stopSpeaking(): void {
  if ('speechSynthesis' in window) window.speechSynthesis.cancel();
}
